import { createContext, useEffect, useState } from 'react'
import { borrarVenta, buscarVentaPorId, obtenerVentasSinEntregar, terminarVenta, validateProductsAvailability } from '../services/PedidoService';
import Proptypes from 'prop-types';
import { obtenerListadoProductosEnStock, obtenerListadoProductosParaDevolucion } from '../services/productServices';
import { compareText, incluyeRecargo } from '../extras/utils';
import { MEDIO_DE_PAGO, PEDIDO_ESTADO } from '../constants';

export const PedidosContext = createContext();

const initialState = {
    compradorId: "",
    numero: "",
    precioTotal: 0,
    totalDevuelto: 0,
    pagoEfectivo: 0,
    pagoCredito: 0,
    pagoNotaCredito: null,
    porcentajeDevolucion: 0,
    medioDePago: MEDIO_DE_PAGO.NINGUNO,
    otros: 0,
    observaciones: "",
    origenVenta: "SHOWROOM",
    estado: PEDIDO_ESTADO.PENDIENTE,
    fecha: null,
    deleted: false
}

export default function PedidosContextProvider({ children }) {
    const [ventas, setVentas] = useState([]);
    const [productos, setProductos] = useState([]);
    const [devoluciones, setDevoluciones] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [devolucionesFiltradas, setDevolucionesFiltradas] = useState([]);
    const [listado, setListado] = useState([]);
    const [subtotal, setSubtotal] = useState(0);
    const [totalDevuelto, setTotalDevuelto] = useState(0);
    const [ventaActual, setVentaActual] = useState({ ...initialState });
    const [recargo, setRecargo] = useState(0);
    const [totalFinal, setTotalFinal] = useState(0);
    const [saldoCliente, setSaldoCliente] = useState(-1);
    const [perPage, setPerPage] = useState(10)

    const changeRowsPerPage = (perPage) => {
        setPerPage(perPage)
    }

    // Trae todos los pedidos que aun no fueron entregados para exhibir en la tabla principal
    async function obtenerVentas() {
        const ventasSinEntregar = await obtenerVentasSinEntregar();
        if (ventasSinEntregar) setVentas(ventasSinEntregar);
    }

    // Trae los productos disponibles para ser comprados
    async function obtenerProductos() {
        const productosEnStock = await obtenerListadoProductosEnStock();
        if (productosEnStock) setProductos(productosEnStock);
        else setProductos([]);
    }

    // Trae los productos que pueden ser devuelto por el cliente
    async function obtenerDevoluciones() {
        const productosParaDevolver = await obtenerListadoProductosParaDevolucion();
        if (productosParaDevolver) setDevoluciones(productosParaDevolver);
        else setDevoluciones([]);
    }


    // Añade un producto a la lista de comprados
    function añadirProducto(_id) {
        let producto;
        if (!_id) return;
        const nuevoListado = productos.filter((prod) => {
            if (prod?._id && prod._id === _id) {
                producto = prod;
                return false;
            }
            return true;
        })
        if (producto) {
            setProductos(nuevoListado);
            const productoAñadido = { ...producto, operacion: 'Compra' }

            if (!listado.find(prod => prod._id === productoAñadido._id)) {
                setListado(prev => [...prev, productoAñadido]);
            } else {
                alert("Producto duplicado");
            }

        }
    }
    // Añade un producto a la lista de devoluciones
    function añadirDevolucion(_id) {
        let producto;
        if (!_id) return;
        const nuevoListado = devoluciones.filter((prod) => {
            if (prod?._id && prod._id === _id) {
                producto = prod;
                return false;
            }
            return true;
        })

        if (producto) {
            const productoAñadido = { ...producto, operacion: 'Devolución' }
            setDevoluciones(nuevoListado);
            if (!listado.find(prod => prod._id === productoAñadido._id)) {
                setListado(prev => [...prev, productoAñadido]);
            } else {
                alert("Producto duplicado");
            }
        }
    }

    // Borra una venta (sin terminar) segun su _id
    async function borrarUnaVenta(_id) {
        const exito = await borrarVenta(_id);
        if (exito) await obtenerVentas();
        return exito;
    }

    // busca y remueve un item de la lista de la compra/devolución, luego pone el producto en la lista que corresponda
    function quitarDeLista(_id) {
        const deleted = listado.find(prod => prod._id === _id)
        const operacion = deleted.operacion;
        delete deleted.operacion;

        if (operacion === 'Devolución') setDevoluciones(prev => [...prev, deleted]);
        if (operacion === 'Compra') setProductos(prev => [...prev, deleted]);

        setListado(prev => prev.filter((item) => item._id !== _id));
    }

    // Dada una Id busca una venta para cargarla y continuar su edición
    async function obtenerVentaPorId(_id) {
        const venta = await buscarVentaPorId(_id);
        if (venta) {
            if (venta.ventas && venta.devoluciones) {
                for (const productoVendido of venta.productosVendidos) {
                    const productoAñadido = { ...productoVendido, operacion: 'Compra' }
                    setListado(prev => [...prev, productoAñadido]);
                }
                for (const productoDevuelto of venta.devoluciones) {
                    const productoAñadido = { ...productoDevuelto, operacion: 'Devolución' }
                    setListado(prev => [...prev, productoAñadido]);
                }
            }
            venta.pagoNotaCredito = venta.pagadoConNotaCredito
            delete venta.pagadoConNotaCredito
            delete venta.productosVendidos;
            setVentaActual(venta);
            return true;
        }
        return false;
    }

    // modifica el campo de la venta pasado y le asigna un valor 
    function modificarVenta(campo, value, saldo = -1) {
        setVentaActual(prev => { return { ...prev, [campo]: value } });
        if (saldo >= 0) setSaldoCliente(saldo);
    }

    // Devuelve la venta al estado inicial
    function limpiarVenta() {
        setVentaActual({ ...initialState });
    }

    function calcularTotales() {
        let subtotalCalculado = listado.reduce((subtotal, prod) => {
            if (prod.operacion === 'Compra') return subtotal + prod.precio
            else return subtotal;
        }, 0);
        let totalDevCalculado = listado.reduce((subtotal, prod) => {
            if (prod.operacion === 'Devolución') return subtotal + prod.precio
            else return subtotal;
        }, 0);
        let totalRecargado = 0;
        if (incluyeRecargo(ventaActual.medioDePago) && recargo > 0 && ventaActual.otros > 0) {
            totalRecargado = (ventaActual.otros * recargo) / 100;
            setVentaActual(prev => { return { ...prev, recargo: totalRecargado } });
        }

        setSubtotal(subtotalCalculado.toFixed(2));
        setTotalDevuelto(totalDevCalculado.toFixed(2));
        setTotalFinal((subtotalCalculado - totalDevCalculado + totalRecargado));
    }

    // Filtra en la tabla que corresponda segun el value( 0 productos/ 1 devoluciones) aquellos productos en los que alguno de los campos datos
    // coincida con el valor de search.
    // Si el campo search se vacia, vuelve a rellenar las tablas con todos los productos disponibles
    function actualizarListas(search, value) {
        if (search && search !== '') {
            if (value === 0) { // Tab de Ventas
                const filtrados = productos.filter((prod) => {
                    return compareText(['descripcion', 'codigo'], prod, search);
                });
                setFilteredProducts(filtrados);
            }
            else { // Tab devoluciones
                const filtrados = devoluciones.filter((prod) => {
                    return compareText(['descripcion', 'codigo'], prod, search);
                });
                setDevolucionesFiltradas(filtrados);
            }
        } else {
            setFilteredProducts(productos);
            setDevolucionesFiltradas(devoluciones);
        }
    }

    function buscarPorCodigo(codigo, value) {
        if (codigo && codigo.length === 9) {
            const splitResult = codigo.split(".");
            if (value === 0) { // Tab de Ventas
                const filtrados = productos.filter(prod => {
                    return prod.UserId === splitResult[0] && prod.codigoProducto === splitResult[1];
                });
                setFilteredProducts(filtrados);
            }
            else { // Tab devoluciones
                const filtrados = devoluciones.filter((prod) => {
                    return prod.UserId === splitResult[0] && prod.codigoProducto === splitResult[1];
                });
                setDevolucionesFiltradas(filtrados);
            }
        } else if (codigo && codigo === "") {
            setFilteredProducts(productos);
            setDevolucionesFiltradas(devoluciones);
        }
    }

    // Limpia la venta actual
    function clearTab() {
        setVentaActual({ ...initialState });
        setListado([]);
        setSubtotal(0);
        setTotalFinal(0);
        obtenerVentas();
        obtenerDevoluciones();
        obtenerProductos();
        setSaldoCliente(-1);
    }


    async function crearVenta(extra = {}) {
        const venta = {
            ventas: [],
            devoluciones: [],
            numero: ventaActual?.numero !== "" ? ventaActual.numero : null,
            compradorId: ventaActual.compradorId,
            pagoEfectivo: totalFinal > 0 ? ventaActual.pagoEfectivo : 0,
            pagoCredito: totalFinal > 0 ? ventaActual.pagoCredito : 0,
            otros: totalFinal > 0 ? (ventaActual.otros) : 0,
            origenVenta: ventaActual.origenVenta,
            estado: ventaActual.estado,
            medioDePago: ventaActual.medioDePago,
            observaciones: ventaActual.observaciones,
            recargo: ventaActual.recargo,
            pagadoConNotaCredito: totalFinal > 0 ? ventaActual.pagoNotaCredito : 0,
            tiendaNubeId: ventaActual.tiendaNubeId
        }

        // Si se esta modificando una venta existente, se envia el _id para modificar en vez de crear una nueva venta
        if (ventaActual._id) venta._id = ventaActual._id;

        // Agregar datos de rastreo si existen
        if (extra.codigoRastreo) venta.codigoRastreo = extra.codigoRastreo;
        if (extra.linkRastreo) venta.linkRastreo = extra.linkRastreo;

        listado.forEach(producto => {
            if (producto.operacion === 'Compra') {
                venta.ventas.push(producto._id);
            } else if (producto.operacion === 'Devolución') {
                venta.devoluciones.push(producto._id);
            }
        })

        const { message, reservados, notaCredito, saldoInsuficiente } = await terminarVenta(venta);
        await obtenerVentas();
        clearTab(); // Limpia todo independientemente del resultado de la venta
        return { message, reservados, notaCredito, saldoInsuficiente };
    }

    async function validateProductsForSale() {
        const ventas = [];
        listado.forEach(producto => {
            if (producto.operacion === 'Compra') {
                ventas.push(producto._id);
            }
        })

        return await validateProductsAvailability(ventas);
    }

    function marcarProductosNoDisponibles(ids) {
        setListado(prev => {
            return prev.map(prod => {
                if (ids.includes(prod._id)) {
                    return { ...prod, operacion: 'No disponible' }
                } else if (prod.operacion === 'No disponible') {
                    return { ...prod, operacion: 'Compra' }
                }
                return prod;
            })
        });
        calcularTotales();
    }

    useEffect(() => {
        calcularTotales();
    }, [recargo, listado.length, ventaActual.otros, ventaActual.medioDePago]);

    useEffect(() => { setDevolucionesFiltradas(devoluciones) }, [devoluciones.length]);
    useEffect(() => { setFilteredProducts(productos) }, [productos.length]);

    return <PedidosContext.Provider value={{
        listado, ventaActual, ventas, subtotal, filteredProducts, devolucionesFiltradas, totalFinal, recargo, saldoCliente, totalDevuelto, perPage,
        modificarVenta, setListado, borrarUnaVenta, quitarDeLista, añadirProducto, añadirDevolucion, obtenerDevoluciones, obtenerVentas, buscarPorCodigo,
        obtenerProductos, obtenerVentaPorId, limpiarVenta, actualizarListas, crearVenta, clearTab, setRecargo, validateProductsForSale, marcarProductosNoDisponibles, changeRowsPerPage
    }}>
        {children}
    </PedidosContext.Provider>
}

PedidosContextProvider.propTypes = {
    children: Proptypes.element.isRequired
}